import { concat, interval, Observable, timer } from 'rxjs';
import { ignoreElements, map, take } from 'rxjs/operators';
import { StreamStatus } from './api.types';

/**
 * Progress streaming, shared by both pipelines.
 *
 * Kept out of app.service.ts on purpose: that file is tabular's *contract*,
 * and an EventSource wrapper is a utility. Both real services open the same
 * kind of stream and both demo services fake it the same way, so the two
 * helpers live here and neither pipeline owns them.
 */

/**
 * Wraps EventSource in an Observable that emits each message and completes on
 * any terminal event (including status:'error').
 *
 * The `status` field is forwarded as-is on next() rather than dropped: a
 * status:'error' message means the BACKEND JOB failed over an otherwise-
 * healthy connection (the SSE stream itself stayed open, HTTP 200 the whole
 * time) — a fundamentally different situation from this Observable's own
 * error() channel, which is reserved for actual transport/connection failures
 * (source.onerror below). Consumers (page-shell's subscribeToStream) branch on
 * s.status to skip reconnecting for the former — retrying a job that already
 * reported its own failure would just replay the identical failure after a
 * pointless delay — while still reconnecting once for the latter.
 */
export function sseStream(url: string): Observable<StreamStatus> {
  return new Observable(observer => {
    const source = new EventSource(url);
    source.onmessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data) as { message: string; status?: string };
      observer.next({ message: data.message, status: data.status });
      // The backend's three terminal statuses. 'canceled' has to be here too: without
      // it the stream is never closed from this side, the server closes it instead, and
      // EventSource's auto-reconnect turns a clean cancel into a transport error.
      if (data.status === 'done' || data.status === 'error' || data.status === 'canceled') {
        source.close();
        observer.complete();
      }
    };
    source.onerror = () => { source.close(); observer.error('SSE connection failed'); };
    return () => source.close();
  });
}

/**
 * Demo counterpart: emits `messages` one every `stepMs`, then stays silent for
 * `tailMs` before completing.
 *
 * The trailing pause is not decoration. page-shell navigates on complete(),
 * so without it the last message would flash and vanish in the same frame.
 */
export function fakeStream(messages: string[], stepMs: number, tailMs: number): Observable<StreamStatus> {
  return concat(
    interval(stepMs).pipe(take(messages.length), map(i => ({ message: messages[i] }))),
    timer(tailMs).pipe(ignoreElements()),
  );
}
